import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getDomainAmazonOffers, getPageSummaries } from './api';
import { useDomain } from './DomainContext';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import Alert from './ui/Alert';
import Spinner from './ui/Spinner';
import Sparkline from './ui/Sparkline';
import { formatCount, metricSlug } from './pageMetrics';
import { mstDayWindow } from './domainMetrics';
import {
  formatCtr, dailySeries, productLabel, amazonUrlFor, sortOfferRows,
} from './offerMetrics';
import SiteSectionNav from './ui/SiteSectionNav';

const CLICK_COLOR = '#16a34a';
const IMPR_COLOR = '#fd7e14';
const DAY_OPTIONS = [7, 30, 90];

const Dash = () => <span className="muted">—</span>;

const chipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', margin: '0 4px 4px 0',
  border: '1px solid var(--color-border)', borderRadius: 999, fontSize: 12, background: 'var(--color-surface)',
};

const Kpi = ({ label, value, hint }) => (
  <div className="kpi" title={hint}>
    <div className="kpi-label">{label}</div>
    <div className="kpi-value">{value}</div>
  </div>
);

/* Sortable header: descending first, since bigger is the interesting end. */
const SortTh = ({ k, sort, setSort, children, title, num = true }) => (
  <th className={`sortable${num ? ' num' : ''}`} title={title}
      aria-sort={sort === k ? 'descending' : undefined} onClick={() => setSort(k)}>
    {children}{sort === k ? ' ▼' : ''}
  </th>
);

const Counts = ({ row }) => (
  <>
    <td className="num">{row.impressions > 0 ? formatCount(row.impressions) : <Dash />}</td>
    <td className="num">{row.clicks > 0 ? formatCount(row.clicks) : <Dash />}</td>
    <td className="num">{formatCtr(row.ctr)}</td>
  </>
);

const Trend = ({ row, dayKeys }) => (
  <td>
    <Sparkline series={[
      { key: 'impressions', color: IMPR_COLOR, data: dailySeries(row.impressions_daily, dayKeys) },
      { key: 'clicks', color: CLICK_COLOR, data: dailySeries(row.clicks_daily, dayKeys) },
    ]} />
  </td>
);

const TrendTh = () => (
  <th style={{ minWidth: 130 }}>
    Trend
    <div style={{ fontWeight: 400, fontSize: '10px', marginTop: '2px' }}>
      <span style={{ color: IMPR_COLOR }}>&#9473; impr</span>{' '}
      <span style={{ color: CLICK_COLOR }}>&#9473; clicks</span>
    </div>
  </th>
);

const PLACEMENT_LABELS = {
  card: 'Card', quick: 'Quick pick', table: 'Table', inline: 'In text',
};

/**
 * Amazon offer performance for one site: which products get seen and clicked,
 * on which pages, in which layout, and (once offers are tested) which variant.
 * Our own Snowplow counts; Amazon's orders and revenue come from the
 * Associates report on the Domains page.
 */
const DomainOffers = () => {
  const { domainId } = useParams();
  const { domains, selectedDomain } = useDomain();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productSort, setProductSort] = useState('clicks');
  const [pageSort, setPageSort] = useState('clicks');
  const [openAsin, setOpenAsin] = useState(null);

  const domain = selectedDomain?.id === domainId ? selectedDomain : domains.find((d) => d.id === domainId) || null;
  const hostname = domain?.hostname || domain?.name || domainId;

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    const [offers, pageList] = await Promise.all([
      getDomainAmazonOffers(domainId, days, refresh),
      getPageSummaries(domainId).catch(() => []),
    ]);
    setData(offers);
    setPages(pageList || []);
    setLoading(false);
  }, [domainId, days]);

  useEffect(() => { load(); }, [load]);

  const pageTitle = useMemo(() => {
    const m = {};
    pages.forEach((p) => { m[metricSlug(p.slug || p.id)] = p.title || p.slug; });
    return m;
  }, [pages]);

  const titleFor = (slug) => (slug === 'home' ? 'Home page' : pageTitle[metricSlug(slug)] || `/${slug}`);
  const editLink = (slug) => (slug === 'home' ? null : `/domain/${domainId}/pages/${encodeURIComponent(slug)}/edit`);

  const dayKeys = (data && data.day_keys) || mstDayWindow(days);
  const totals = (data && data.totals) || {};
  const products = useMemo(() => sortOfferRows(data && data.products, productSort), [data, productSort]);
  const pageRows = useMemo(() => sortOfferRows(data && data.pages, pageSort), [data, pageSort]);
  const variants = (data && data.variants) || [];
  const empty = !loading && data && !data.error && !(totals.impressions || totals.clicks);

  return (
    <div className="page">
      <PageHeader
        title="Offers"
        subtitle={`${hostname}: Amazon offer impressions and clicks, by product and by page`}
        actions={(
          <>
            <label className="sr-only" htmlFor="offers-days">Period</label>
            <select id="offers-days" className="select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {DAY_OPTIONS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
            </select>
            <button type="button" className="btn btn-secondary" onClick={() => load(true)} disabled={loading}>
              Refresh
            </button>
          </>
        )}
      />
      <SiteSectionNav section="growth" />

      {data && data.error && <Alert type="error">Could not load offer metrics from BigQuery.</Alert>}

      <p className="muted small" style={{ marginTop: 0 }}>
        Our own counts from the site&apos;s Snowplow events: an impression is an offer at least half in view (once per
        offer per page load), a click is a click on any of its Amazon links. Amazon&apos;s own clicks, orders and
        earnings come from the Associates report and will not match exactly. The last {days} complete MST days,
        today excluded.
      </p>

      {loading && !data ? (
        <Spinner label="Loading offer metrics…" />
      ) : empty ? (
        <EmptyState
          title="No offer impressions yet"
          message={`Nothing recorded on ${hostname} in the last ${days} days. Counting starts once the site serves offer-tracking.js and an affiliate offer is seen.`}
        />
      ) : data && !data.error ? (
        <>
          <div className="kpi-grid" style={{ marginBottom: 20 }}>
            <Kpi label="Offer impressions" value={formatCount(totals.impressions || 0)} />
            <Kpi label="Clicks to Amazon" value={formatCount(totals.clicks || 0)} />
            <Kpi label="CTR" value={formatCtr(totals.ctr)} hint="Clicks / offer impressions" />
            <Kpi label="Products" value={formatCount(totals.products || 0)} />
            <Kpi label="Pages with offers" value={formatCount(totals.pages || 0)} />
          </div>

          <div className="card">
            <div className="card-header"><h2 className="card-title">By product</h2></div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: 56 }} aria-label="Image" />
                    <SortTh k="title" sort={productSort} setSort={setProductSort} num={false}>Product</SortTh>
                    <SortTh k="impressions" sort={productSort} setSort={setProductSort}>Impr</SortTh>
                    <SortTh k="clicks" sort={productSort} setSort={setProductSort}>Clicks</SortTh>
                    <SortTh k="ctr" sort={productSort} setSort={setProductSort} title="Clicks / impressions">CTR</SortTh>
                    <TrendTh />
                    <th>Pages</th>
                    <th title="Where on the page it was shown">Layout</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => {
                    const url = amazonUrlFor(p.asin);
                    const open = openAsin === p.asin;
                    return (
                      <tr key={p.asin}>
                        <td>
                          {p.image_url
                            ? <img src={p.image_url} alt="" loading="lazy" referrerPolicy="no-referrer"
                                   style={{ width: 48, height: 48, objectFit: 'contain' }} />
                            : null}
                        </td>
                        <td style={{ maxWidth: 320 }}>
                          <div style={{ fontWeight: 500 }}>{productLabel(p)}</div>
                          <div className="muted small">
                            {url ? <a href={url} target="_blank" rel="noopener noreferrer">{p.asin}</a> : p.asin}
                            {p.in_library
                              ? <> · <Link to={`/domain/${domainId}/products?asin=${encodeURIComponent(p.asin)}`}>in library</Link></>
                              : null}
                          </div>
                        </td>
                        <Counts row={p} />
                        <Trend row={p} dayKeys={dayKeys} />
                        <td style={{ minWidth: 200 }}>
                          {(open ? p.pages : p.pages.slice(0, 3)).map((pg) => (
                            <span key={pg.slug} style={chipStyle}
                                  title={`${formatCount(pg.impressions)} impr · ${formatCount(pg.clicks)} clicks · ${formatCtr(pg.ctr)} CTR`}>
                              {editLink(pg.slug) ? <Link to={editLink(pg.slug)}>{titleFor(pg.slug)}</Link> : titleFor(pg.slug)}
                              <span className="muted">{pg.clicks}/{pg.impressions}</span>
                            </span>
                          ))}
                          {p.pages.length > 3 && (
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpenAsin(open ? null : p.asin)}>
                              {open ? 'fewer' : `+${p.pages.length - 3} more`}
                            </button>
                          )}
                        </td>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>
                          {p.placements.map((pl) => (
                            <div key={pl.placement} title={`${formatCount(pl.impressions)} impr · ${formatCount(pl.clicks)} clicks`}>
                              {PLACEMENT_LABELS[pl.placement] || pl.placement}{' '}
                              <span className="muted">{formatCtr(pl.ctr)}</span>
                            </div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h2 className="card-title">By page</h2></div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <SortTh k="slug" sort={pageSort} setSort={setPageSort} num={false}>Page</SortTh>
                    <SortTh k="impressions" sort={pageSort} setSort={setPageSort}>Impr</SortTh>
                    <SortTh k="clicks" sort={pageSort} setSort={setPageSort}>Clicks</SortTh>
                    <SortTh k="ctr" sort={pageSort} setSort={setPageSort} title="Clicks / impressions">CTR</SortTh>
                    <TrendTh />
                    <th>Top products (clicks / impr)</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((pg) => (
                    <tr key={pg.slug}>
                      <td style={{ maxWidth: 320 }}>
                        <strong>{titleFor(pg.slug)}</strong>
                        <div className="muted mono" style={{ fontSize: 12 }}>/{pg.slug === 'home' ? '' : pg.slug}</div>
                      </td>
                      <Counts row={pg} />
                      <Trend row={pg} dayKeys={dayKeys} />
                      <td className="small" style={{ minWidth: 220 }}>
                        {pg.products.slice(0, 4).map((p) => (
                          <div key={p.asin}>
                            {productLabel(p)} <span className="muted">{p.clicks}/{p.impressions}</span>
                          </div>
                        ))}
                        {pg.products.length > 4 && <div className="muted">+{pg.products.length - 4} more</div>}
                      </td>
                      <td className="actions">
                        {editLink(pg.slug) && <Link className="btn btn-secondary btn-sm" to={editLink(pg.slug)}>Edit</Link>}
                        <a className="btn btn-ghost btn-sm" href={`https://${hostname}/${pg.slug === 'home' ? '' : `${pg.slug}/`}`}
                           target="_blank" rel="noopener noreferrer">Live ↗</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {variants.length > 0 && (
            <div className="card">
              <div className="card-header"><h2 className="card-title">Offer variants</h2></div>
              <p className="muted small">Offers marked with data-offer-variant, for comparing different offers on the same pages or sitewide.</p>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr><th>Variant</th><th className="num">Impr</th><th className="num">Clicks</th><th className="num">CTR</th></tr>
                  </thead>
                  <tbody>
                    {variants.map((v) => (
                      <tr key={v.variant}><td className="mono">{v.variant}</td><Counts row={v} /></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default DomainOffers;
